import {
  convertQueryCommandToQueryString,
  Equal,
  IQueryCommandOptions,
  mergeQueryCommandOptions,
} from '@llws/dynamic-router-utils';
import { IBuildings, ITourBuildingSettings } from '@llws/lift-entity-interfaces';
import vm from '@/main';
import {
  decodeHtml,
  IFilterPayloadV2,
  IModifyPayload,
  ISelectOption,
  uniqueMap,
} from '@/shared';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';

type TAgentSettings = {
  id: number;
  status: string;
};

type TClienProperties = {
  text: string;
  value: number;
  agents: Array<TAgentSettings>,
};

type TPropertiesWithouPhoneNumber = {
  id: number;
  buildingName: string;
};

interface ITourBuildingSettingsState {
  clientProperties: Array<TClienProperties>,
  clientPropertiesOptions: Array<ISelectOption>,
  clientCitiesOptions: Array<ISelectOption>,
  tourBuildingSettings: Array<ITourBuildingSettings>,
  tourPropertiesWithoutPhoneNumber: Array<TPropertiesWithouPhoneNumber>,
}

// Base store configuration
const agentGatewayResourcePathAgent: string = '/tour-api-agent';
const adminGatewayResourcePathAdmin: string = '/tour-api-admin';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {};

const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ITourBuildingSettings>({
  adminGatewayResourcePath: adminGatewayResourcePathAdmin,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

const tourBuildingSettingsFilters: IQueryCommandOptions = {
  alias: 'buildingSettings',
  relations: [
    {
      path: 'building',
      alias: 'b',
    },
    {
      path: 'b.city',
      alias: 'city',
    },
  ],
  select: [
    'agents',
    'id',
    'status',
    'clientId',
    'buildingId',
    'incrementMinutes',
    'increment',
    'tourDuration',
    'timeslotBuffer',
    'timeBookingDelay',
    'timeslotBufferBeforeTour',
    'timeslotBufferAfterTour',
    'includeBufferBeginning',
    'includeBufferAfter',
    'hasCustomEmail',
    'customEmail',
    'replyToEmail',
    'hasVirtualTour',
    'useBuildingTimezone',
    'extraActionEmailEnabled',
    'extraActionEmail',
    'trackingEmailEnabled',
    'trackingEmail',
    'tourAssignment',
    'autoConfirmBookings',
    'bookingWindowDays',
    'widgetTitleSource',
    'b.buildingName',
    'b.streetNumber',
    'b.streetName',
    'b.status',
  ],
  aggregations: [
    {
      propertyAlias: 'agents',
      table: 'tour_users_has_buildings',
      aggregate: 'COUNT',
      column: 'id',
      whereCommands: [
        Equal('buildingId', 'buildingSettings.buildingId'),
      ],
    },
  ],
  limit: 9999,
};

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    clientProperties: [] as Array<TClienProperties>,
    clientPropertiesOptions: [] as Array<ISelectOption>,
    clientCitiesOptions: [] as Array<ISelectOption>,
    tourBuildingSettings: [] as Array<ITourBuildingSettings>,
    tourPropertiesWithoutPhoneNumber: [] as Array<TPropertiesWithouPhoneNumber>,
  },

  getters: {
    ...base.getters,
    clientProperties(state: ITourBuildingSettingsState): (TClienProperties)[] {
      return state.clientProperties;
    },
    clientPropertiesOptions(state: ITourBuildingSettingsState): ISelectOption[] {
      return state.clientProperties.map((property) => ({ text: property.text, value: property.value }));
    },
    clientCitiesOptions(state: ITourBuildingSettingsState): ISelectOption[] {
      return state.clientCitiesOptions;
    },
    tourBuildingSettings(state: ITourBuildingSettingsState): ITourBuildingSettings[] { return state.tourBuildingSettings ? state.tourBuildingSettings : []; },
    tourPropertiesWithoutPhoneNumber(state: ITourBuildingSettingsState): TPropertiesWithouPhoneNumber[] { return state.tourPropertiesWithoutPhoneNumber; },
  },

  mutations: {
    ...base.mutations,
    setClientProperties: (state: ITourBuildingSettingsState, payload: Array<ITourBuildingSettings>) => {
      const properties: TClienProperties[] = payload.map((tourBuilding) => {
        const userSettings: TAgentSettings[] = tourBuilding.building?.tourUserHasBuildings?.map(({ user }) => ({
          id: user!.id!,
          status: user?.tourUserSettings?.[0]?.status || '',
        })) || [];

        return {
          text: decodeHtml(tourBuilding.building!.buildingName),
          value: tourBuilding.buildingId,
          agents: userSettings,
        };
      }) || [];
      state.clientProperties = properties;
    },
    setClientCitiesOptions: (state: ITourBuildingSettingsState, payload: Array<ITourBuildingSettings>) => {
      state.clientCitiesOptions = uniqueMap(payload, (tour) => tour.building?.city?.id, ((tour) => ({
        text: decodeHtml(tour.building?.city?.cityName),
        value: tour.building?.city?.id!,
      })));
    },
    setTourBuildingSettings(state: ITourBuildingSettingsState, payload: Array<ITourBuildingSettings>) {
      state.tourBuildingSettings = payload;
    },
    setTourPropertiesWithoutPhoneNumber(state: ITourBuildingSettingsState, payload: Array<TPropertiesWithouPhoneNumber>) {
      state.tourPropertiesWithoutPhoneNumber = payload;
    },
  },

  actions: {
    ...base.actions,
    saveTourBuildingSettings: async (
      ctx: IAction<IStateStrict<ITourBuildingSettings>>,
      payload: IModifyPayload,
    ) => {
      const response = payload.data?.id ? await adminRequest.put(`${adminGatewayResourcePathAdmin}/tour_building_settings`, payload.data)
        : await adminRequest.post(`${adminGatewayResourcePathAdmin}/tour_building_settings`, payload.data);
      ctx.commit('setTourBuildingSettings', response.data);
    },
    getTourBuildingSettings: async (ctx: IAction<IStateStrict<ITourBuildingSettings>>, payload?: IFilterPayloadV2) => {
      const filters = mergeQueryCommandOptions([tourBuildingSettingsFilters, payload?.filters || {}]);
      const url = vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin;
      const response = await adminRequest.get(`${url}/tour_building_settings${convertQueryCommandToQueryString(filters)}&userId=${payload?.additional?.userId || ''}`);

      ctx.commit('setClientProperties', response.data);
      ctx.commit('setClientCitiesOptions', response.data);
      ctx.commit('setTourBuildingSettings', response.data);
      return response.data;
    },
    getTourPropertiesWithoutPhoneNumber: async (ctx: IAction<IStateStrict<IBuildings>>, clientId: number) => {
      const response = await adminRequest.get(`${adminGatewayResourcePathAdmin}/property-without-phone/${clientId}`);
      ctx.commit('setTourPropertiesWithoutPhoneNumber', response);
    },
    applyDefaultTourSettings: async (_ctx: IAction<IStateStrict<ITourBuildingSettings>>, payload?: IFilterPayloadV2) => {
      const response = await adminRequest.post(`${adminGatewayResourcePathAdmin}/set-global-property-settings/${payload?.clientId}`, payload?.additional ?? {});
      return response;
    },
  },
};
